import { ExecutionStatus } from '@/types/statuses.ts';
import type { Dataset, TriggerDataset } from '@/types/datasets.ts';
import { PaginatedResponse } from '@/types/common.ts';
import type { CreatorPrincipalType } from '@/types/automations.ts';
import { ExecutionModelSettings } from '@/types/llmModels.ts';

export type RunsStats = {
  runs_count: number;
  in_progress_count: number;
  ask_user_count: number;
  completed_count: number;
  completed_with_issues_count: number;
  failed_count: number;
};
export type ExtractEntityToRerun = {
  extract_entity_index: number;
  fields: string[] | 'all' | 'failed';
};

export type ExtractEntityRerunOptions = ExtractEntityToRerun[] | 'all' | 'failed';

export type RerunConfig = {
  step_path: string;
  children_to_rerun: 'all' | 'failed'; // Rerun all children or only failed child scopes where supported
  extract_entities_to_run: ExtractEntityRerunOptions;
};

export type FailedStepsRerunConfig = {
  parent_path: string;
  child_index: number | null;
  include_ask_user: boolean;
  target_step_feedback?: string;
  target_step_paths?: string[];
};

export type ExecutionBounds = {
  stop_after_step_ids: string[];
};

export type ExecutionConfig = {
  ignore_cache: boolean;
  run_downstream: boolean;
  rerun_config: RerunConfig | null;
  failed_steps_rerun_config: FailedStepsRerunConfig | null;
  execution_bounds: ExecutionBounds | null;
  model_settings: ExecutionModelSettings | null;
};

export type ExecutionConfigRequest = {
  ignore_cache: boolean;
  run_downstream: boolean;
  rerun_config?: RerunConfig | null;
  failed_steps_rerun_config?: FailedStepsRerunConfig | null;
  execution_bounds?: ExecutionBounds | null;
  model_settings?: ExecutionModelSettings | null;
};

export type ReplayRunFromSourceSelection = 'original' | 'active';
export type ReplayRunFromSourceRequest =
  | { selection: 'original'; execution_config?: ExecutionConfigRequest }
  | {
      selection: 'active';
      expected_activation: import('./automationPublications.ts').ActivationFence;
      execution_config?: ExecutionConfigRequest;
    };

export type Execution = {
  id: string;
  index: number;
  created_at: string;
  completed: string | null;
  step_feedback: Record<string, string>;
  config: ExecutionConfig;
  intervention_response?: InterventionResponseAudit | null;
};

export type InterventionResponseAudit = {
  id: string;
  paused_execution_id: string;
  paused_step_run_id: string;
  target_step_path: string;
  values: Record<string, string>;
  binding_name: string;
  rerun_execution_id: string | null;
  created_at: string;
  created_by_label: string;
};

export type HumanDecisionRequest = {
  id: string;
  run_id: string;
  automation_id: string;
  step_id: string;
  step_path: string;
  status: 'pending' | 'claimed' | 'resolved' | 'rejected' | 'cancelled' | 'expired' | 'superseded';
  request_schema_version: string;
  response_fields: Array<{
    field_id: string;
    label: string;
    field_type: 'enum' | 'text' | 'boolean' | 'number' | 'date';
    required: boolean;
    enum_values: string[];
    max_length: number | null;
  }>;
  require_reason: boolean;
  request_payload: Record<string, unknown>;
  evidence_snapshot: Record<string, unknown>;
  expires_at: string | null;
  claimed_by: string;
  claimed_at: string | null;
  resolved_by: string;
  resolved_at: string | null;
  resolution_provenance?: {
    kind: 'human' | 'automated_test';
    actor: { kind: string; user_id: string; principal?: string };
    operation_id?: string | null;
  } | null;
  response_payload: Record<string, unknown>;
  response_reason: string;
  revision: number;
  continuation_execution_id: string | null;
  can_claim: boolean;
  can_release_claim: boolean;
  can_resolve: boolean;
  can_terminalize: boolean;
};

export type HumanDecisionInboxItem = {
  id: string;
  run_id: string;
  automation_id: string;
  status: 'pending' | 'claimed' | 'resolved' | 'rejected' | 'cancelled' | 'expired' | 'superseded';
  summary: string;
  expires_at: string | null;
  claimed_by: string;
  claimed_at: string | null;
  created_at: string;
};

export enum RunMode {
  Operational = 'operational',
  Test = 'test',
  EffectfulTest = 'effectful_test',
}

export enum RunOrigin {
  Manual = 'manual',
  Portal = 'portal',
  ExternalTrigger = 'external_trigger',
  Scheduled = 'scheduled',
  Builder = 'builder',
  HealthCheck = 'health_check',
  SubAutomation = 'sub_automation',
}

export enum RunProvenanceState {
  Published = 'published',
  Draft = 'draft',
  Historical = 'historical',
}

export type RunFilterOption<T extends string> = {
  label: string;
  value: T;
};

export const RUN_STATUS_FILTER_OPTIONS: RunFilterOption<ExecutionStatus>[] = [
  { label: 'In Progress', value: ExecutionStatus.Active },
  { label: 'Needs input', value: ExecutionStatus.AskUser },
  { label: 'Completed', value: ExecutionStatus.Completed },
  { label: 'Completed with issues', value: ExecutionStatus.PartiallyCompleted },
  { label: 'Errors', value: ExecutionStatus.Errors },
];

export const DEFAULT_RUN_STATUSES = RUN_STATUS_FILTER_OPTIONS.map((option) => option.value);

export const isDefaultRunStatusSelection = (statuses: readonly ExecutionStatus[]): boolean =>
  statuses.length === DEFAULT_RUN_STATUSES.length &&
  DEFAULT_RUN_STATUSES.every((status) => statuses.includes(status));

export const RUN_PROVENANCE_FILTER_OPTIONS: RunFilterOption<RunProvenanceState>[] = [
  { label: 'Published', value: RunProvenanceState.Published },
  { label: 'Draft', value: RunProvenanceState.Draft },
  { label: 'Historical', value: RunProvenanceState.Historical },
];

export const getRunOriginFilterOptions = (): RunFilterOption<RunOrigin>[] => [
  { label: 'Manual', value: RunOrigin.Manual },
  { label: 'Portal', value: RunOrigin.Portal },
  { label: 'External', value: RunOrigin.ExternalTrigger },
  { label: 'Scheduled', value: RunOrigin.Scheduled },
  { label: 'Authoring checks', value: RunOrigin.Builder },
  { label: 'Health check', value: RunOrigin.HealthCheck },
  { label: 'Sub-automation', value: RunOrigin.SubAutomation },
];

export const getDefaultRunOrigins = (): RunOrigin[] => Object.values(RunOrigin);

export const RUN_MODE_FILTER_OPTIONS: RunFilterOption<RunMode>[] = [
  { label: 'Operational', value: RunMode.Operational },
  { label: 'Safe execution', value: RunMode.Test },
  { label: 'Effectful execution', value: RunMode.EffectfulTest },
];

export const getDefaultRunModes = (): RunMode[] => [RunMode.Operational];

export const DEFAULT_RUN_PROVENANCE_STATES: RunProvenanceState[] = [
  RunProvenanceState.Published,
  RunProvenanceState.Draft,
  RunProvenanceState.Historical,
];

export const getRunProvenanceQueryStates = (
  selectedStates: RunProvenanceState[],
): RunProvenanceState[] =>
  selectedStates.length === DEFAULT_RUN_PROVENANCE_STATES.length &&
  DEFAULT_RUN_PROVENANCE_STATES.every((state) => selectedStates.includes(state))
    ? []
    : selectedStates;

export type Run = {
  id: string;
  automation_id: string;
  automation_version_id?: string | null;
  automation_version_number?: number | null;
  name: string;
  created_at: string;
  total_runtime: number | null;
  last_run_at: string | null;
  status: ExecutionStatus;
  run_mode: RunMode;
  test_purpose?: 'diagnostic' | 'regression' | null;
  run_origin: RunOrigin;
  automation_portal_id: string | null;
  trigger_step_id: string | null;
  provenance_state?: RunProvenanceState | null;
  was_latest_at_creation?: boolean | null;
  was_published_at_creation?: boolean | null;
  run_mode_label?: string | null;
  preprocessing_error: string | null;
  cancellation?: RunCancellation | null;
  preprocessing_blockers?: RunPreprocessingBlocker[];
  latest_execution: Execution | null;
  liveness?: RunLiveness | null;
  business_outcome_assessment?: BusinessOutcomeAssessment | null;
};

export type RunLivenessState =
  | 'running'
  | 'queued_for_delivery'
  | 'waiting_for_processing'
  | 'waiting_for_input'
  | 'stale_recovery_eligible'
  | 'terminal'
  | 'unavailable';

export type RunLiveness = {
  state: RunLivenessState;
  reason_code: string;
  summary: string;
  observed_at: string;
  active_step: {
    step_id: string;
    full_path: string;
    status: string;
  } | null;
  last_activity_at: string | null;
};

export type BusinessOutcomeAssessment = {
  resolution_state:
    | 'not_configured'
    | 'awaiting_completion'
    | 'evaluating'
    | 'resolved'
    | 'evaluation_unavailable';
  label: string | null;
  state: 'achieved' | 'not_achieved' | 'insufficient_evidence' | null;
  summary: string | null;
  evidence_sources: Array<Record<string, unknown>>;
};

export type RunCancellation = {
  schema_version: 1;
  source: 'user' | 'system';
  actor_id: string | null;
  reason: string;
  cancelled_at: string;
};

export const getRunCancellationSummary = (cancellation: RunCancellation): string => {
  const actor = cancellation.actor_id
    ? `${cancellation.source} ${cancellation.actor_id}`
    : cancellation.source;
  return `Cancelled by ${actor}: ${cancellation.reason}`;
};

export type RunPreprocessingBlocker = {
  dataset_id: string;
  source_type: string;
  reason: string;
  dataset_name: string;
  unprocessed: boolean | null;
  processing: boolean | null;
  processing_error: string | null;
  trigger_id: string | null;
  trigger_step_id: string | null;
  static_dataset_id: string | null;
  linked_dataset_id: string | null;
};

export type RunVersionStateLabel = 'Published' | 'Draft' | 'Historical';

export const getRunVersionLabel = (run: Run): string | null => {
  if (typeof run.automation_version_number === 'number') {
    return `v${run.automation_version_number}`;
  }
  return null;
};

export const getRunVersionStateLabel = (run: Run): RunVersionStateLabel => {
  if (run.provenance_state === RunProvenanceState.Published) return 'Published';
  if (run.provenance_state === RunProvenanceState.Draft) return 'Draft';
  if (run.provenance_state === RunProvenanceState.Historical) return 'Historical';
  if (run.was_published_at_creation === true) return 'Published';
  if (run.was_published_at_creation === false && run.was_latest_at_creation === true)
    return 'Draft';
  if (run.was_published_at_creation === false && run.was_latest_at_creation === false)
    return 'Historical';
  if (run.was_latest_at_creation === true) return 'Draft';
  return 'Historical';
};

export const getRunSourceLabel = (run: Run): string => {
  if (run.test_purpose === 'diagnostic') return 'Diagnostic run';
  if (run.test_purpose === 'regression') return 'Regression run';
  if (run.run_origin === RunOrigin.HealthCheck) return 'Health check';
  if (run.run_origin === RunOrigin.ExternalTrigger) return 'External';
  if (run.run_origin === RunOrigin.Scheduled) return 'Scheduled';
  if (run.run_origin === RunOrigin.SubAutomation) return 'Sub-automation';
  if (run.run_origin === RunOrigin.Builder)
    return run.run_mode === RunMode.EffectfulTest ? 'Effectful execution' : 'Safe execution';
  return run.run_origin === RunOrigin.Portal ? 'Portal' : 'Manual';
};

export const isBuilderTestRun = (run: Run): boolean =>
  run.run_origin === RunOrigin.Builder && run.run_mode !== RunMode.Operational;

export const getRunAutomationVersionLabel = (run: Run): string | null => {
  if (typeof run.automation_version_number === 'number') {
    return `v${run.automation_version_number}`;
  }
  return null;
};

export const getRunProvenanceBooleanLabel = (
  value: boolean | null | undefined,
): 'Yes' | 'No' | 'Unknown' => {
  if (value == null) return 'Unknown';
  return value ? 'Yes' : 'No';
};

const getTestRunGeneratedNamePrefix = (run: Run): string | null => {
  if (run.run_mode === RunMode.Operational) return null;
  if (run.run_origin === RunOrigin.HealthCheck) return 'Health check - ';
  return null;
};

export const getRunDisplayName = (run: Run): string => {
  const prefix = getTestRunGeneratedNamePrefix(run);
  if (!prefix) return run.name;
  if (!run.name.startsWith(prefix)) return run.name;

  return run.name.slice(prefix.length) || run.name;
};

export type Runs = PaginatedResponse<Run>;
export type RunOutputDataset = {
  dataset: Dataset;
  output_id: string;
  step_id: string;
};
export type RunStaticDataset = {
  dataset: Dataset;
  static_dataset_id: string;
};
export type RunLinkedDataset = {
  dataset: Dataset;
  linked_dataset_id: string;
};
export type RunWithDatasets = {
  run: Run;
  input_datasets: TriggerDataset[];
  static_datasets?: RunStaticDataset[];
  linked_datasets?: RunLinkedDataset[];
  output_datasets: RunOutputDataset[];
};
export type RunsWithDatasets = PaginatedResponse<RunWithDatasets>;

export enum ChatMessageStatus {
  RetrievingSteps = 'retrieving_steps',
  RetrievingDatasets = 'retrieving_datasets',
  GeneratingResponse = 'generating_response',
  Completed = 'completed',
}

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_by: string | null;
  created_by_type: CreatorPrincipalType;
  created_by_display_name: string;
  created_by_email: string;
  created_by_label: string;
  created_at: string;
  status: ChatMessageStatus;
  active: boolean;
};
export type ChatMessages = PaginatedResponse<ChatMessage>;
